import * as Sentry from '@sentry/react'
import { BrowserTracing } from '@sentry/tracing'
import 'animate.css'
import 'react-bootstrap-typeahead/css/Typeahead.css'
import 'react-datepicker/dist/react-datepicker.css'
import ReactDOM from 'react-dom'

import { Toast } from '../../../libs/shared/src/components/toast/index'
import { RoleSwitchProvider } from '@zing/active-tenant/contexts/RoleSwitchProvider'
import { APP_ENVIRONMENT } from '../../../libs/shared/src/constants'
import { DarkThemeProvider } from '../../../libs/shared/src/theme'
import { mergedTheme } from '../../../libs/shared/src/assets/theme/mergedTheme'
import { authLayout as baseAuthLayout } from '@zing/white-label/authLayout'
import { authLayout as tenantAuthLayout } from '@zing/active-tenant/authLayout'
import { AuthLayoutConfig } from '../../../libs/shared/src/types/authLayout/types'
import { AuthLayoutProvider } from '../../../libs/shared/src/layouts/AuthLayoutContext'
import { QueryClientWrapper } from '../../../libs/shared/src/api'
import { Provider, store } from '../../../libs/shared/src/store'
import '@zing/shared/i18n/translate/config'
import '@zing/shared/assets/scss/index.scss'
import '@zing/shared/assets/style/form.css'
import '@zing/shared/assets/style/globals.css'
import '@zing/shared/assets/style/steps.css'
import 'virtual:zing-tenant-styles'

import { CreateRoutes } from './routes/routes'
import { ErrorBoundary } from './ErrorBoundary'
import { registerHubRoutes } from '../../../libs/shared/src/components/Breadcrumb/hubRoutesRegistry'
import { hubRoutes } from './routes/hub'

import { buildFallbackUrl, initSmartNav, isLegacyEscapeHatch } from '../../../libs/shared/src/smartNav'
import { registeredFullPaths } from 'virtual:zing-registry'

registerHubRoutes(hubRoutes)

initSmartNav({
  apiBaseUrl: process.env.REACT_APP_BASE_API_URL ?? '',
  tenantDomain: process.env.TENANT_DOMAIN ?? 'localhost',
  pollIntervalMs: parseInt(process.env.UNLEASH_POLL_INTERVAL_MS ?? '15000', 10),
  registeredFullPaths,
  legacyUrl: process.env.REACT_APP_LEGACY_URL ?? '',
  legacyPathPrefixes: (process.env.REACT_APP_LEGACY_PATH_PREFIXES ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean),
})

Sentry.init({
  dsn: 'https://1eb42cdef3724d5f8e5b2c4e9e0fffc3@o446935.ingest.sentry.io/6564991',
  integrations: [new BrowserTracing()],
  tracesSampleRate: 1.0,
  environment: APP_ENVIRONMENT,
})

const mergedAuthLayout: AuthLayoutConfig = {
  ...baseAuthLayout,
  ...(tenantAuthLayout ?? {}),
}

function renderApp(): void {
  ReactDOM.render(
    <ErrorBoundary>
      <Provider store={store}>
        <QueryClientWrapper>
          <AuthLayoutProvider config={mergedAuthLayout}>
            <DarkThemeProvider theme={mergedTheme}>
              <RoleSwitchProvider>
                <ErrorBoundary>
                  <Toast />
                  <CreateRoutes />
                </ErrorBoundary>
              </RoleSwitchProvider>
            </DarkThemeProvider>
          </AuthLayoutProvider>
        </QueryClientWrapper>
      </Provider>
    </ErrorBoundary>,
    document.getElementById('app-root'),
  )
}

// smart-nav is the single routing authority in every environment. The shell always
// mounts; the mount-time guard (useServerFallbackGuard, in RoutesBuilder) redirects
// unmigrated routes to the legacy host after the first authenticated flag poll. Pre-login
// (no JWT) the guard renders in place — pre-login pages never redirect.
//
// EXCEPT for the static escape hatch (REACT_APP_LEGACY_PATH_PREFIXES): those paths belong to
// the legacy in every state, logged in or not, so they cannot wait on a flag poll that
// pre-login can never happen. Without this, a not-logged legacy-only page (e.g. the public
// meeting-room reservation form) has no route in the shell and falls into "*" → NotFound.
// Decided here, before mounting, so that 404 never paints on the way out.
const { pathname, search, hash } = window.location
if (isLegacyEscapeHatch(pathname)) {
  window.location.replace(buildFallbackUrl(pathname, search, hash))
} else {
  renderApp()
}
